import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Sidebar from 'components/Sidebar';
import SearchOverlay from 'components/SearchOverlay';
import MobileHeader from 'components/MobileHeader';
import Button from 'components/Button';
import { getConversations, createConversation } from 'api/companyMapping';
import { useFeedback } from 'contexts/feedback';
import { MapTrifoldIcon, ClockIcon, ArrowRightIcon, SparkleIcon, ChartScatterIcon, ListBulletsIcon } from '@phosphor-icons/react';

export default function CompanyMappingStart() {
    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const [companyWebsite, setCompanyWebsite] = useState('');

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setError } = useFeedback();

    // Fetch recent company-mapping conversations
    const { data: recentMappings = [], isLoading: isLoadingRecent } = useQuery({
        queryKey: ['company-mapping-conversations'],
        queryFn: async () => {
            const conversations = await getConversations({
                agent: 'company-mapping',
                params: { limit: 3 }
            });
            return conversations || [];
        },
        retry: false
    });

    // Create new conversation mutation
    const { mutate: startNewMapping, status: createStatus } = useMutation({
        mutationFn: createConversation,
        onSuccess: (data) => {
            queryClient.invalidateQueries(['company-mapping-conversations']);
            navigate(`/tools/company-mapping/${data._id}`);
        },
        onError: () => {
            setError('Errore durante la creazione della mappatura');
        }
    });

    const handleStartNew = () => {
        if (createStatus === 'pending' || !companyWebsite.trim()) return;
        startNewMapping({ body: { source: 'company-mapping', website: companyWebsite.trim() } });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <Sidebar visible={sidebar} setVisible={setSidebar} />
            <SearchOverlay visible={search} setVisible={setSearch} />
            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                setSearch={setSearch}
                search={search}
            />

            <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-auto pt-14 lg:pt-0 ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                <div className="min-h-full flex flex-col items-center justify-center p-6 lg:p-12">
                    <div className="w-full max-w-3xl">
                        {/* Header */}
                        <div className="text-center mb-10">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-6">
                                <MapTrifoldIcon size={32} weight="duotone" className="text-white" />
                            </div>
                            <h1 className="text-3xl lg:text-4xl font-bold text-black/90 mb-4">
                                Mappatura Aziendale AI
                            </h1>
                            <p className="text-lg text-black/60 max-w-xl mx-auto">
                                Scopri come l&apos;intelligenza artificiale può trasformare i processi della tua azienda.
                                Identifica le opportunità di automazione e ottimizzazione con il supporto dell&apos;AI.
                            </p>
                        </div>

                        {/* Features */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                            <div className="bg-white border border-black/10 rounded-xl p-5">
                                <SparkleIcon size={24} weight="duotone" className="text-blue-500 mb-3" />
                                <h3 className="font-semibold text-black/80 mb-1">Analisi AI</h3>
                                <p className="text-sm text-black/50">
                                    Conversa con l&apos;AI per identificare le aree di miglioramento
                                </p>
                            </div>
                            <div className="bg-white border border-black/10 rounded-xl p-5">
                                <ChartScatterIcon size={24} weight="duotone" className="text-purple-500 mb-3" />
                                <h3 className="font-semibold text-black/80 mb-1">Matrice Visuale</h3>
                                <p className="text-sm text-black/50">
                                    Visualizza le idee in base a impatto e facilità di implementazione
                                </p>
                            </div>
                            <div className="bg-white border border-black/10 rounded-xl p-5">
                                <ListBulletsIcon size={24} weight="duotone" className="text-green-500 mb-3" />
                                <h3 className="font-semibold text-black/80 mb-1">Lista Priorità</h3>
                                <p className="text-sm text-black/50">
                                    Organizza e esporta le tue idee per una roadmap chiara
                                </p>
                            </div>
                        </div>

                        {/* Company Name Input & Start Button */}
                        <div className="bg-white border border-black/10 rounded-xl p-6 mb-12">
                            <label className="block text-sm font-medium text-black/70 mb-2">
                                Sito web aziendale
                            </label>
                            <input
                                type="text"
                                name="website"
                                value={companyWebsite}
                                onChange={(e) => setCompanyWebsite(e.target.value)}
                                placeholder="https://www.tua-azienda.com"
                                className="w-full px-4 py-3 border border-black/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
                            />
                            <Button
                                onClick={handleStartNew}
                                status={createStatus}
                                className={`w-full bg-black text-white hover:bg-black/80 rounded-xl px-8 py-4 text-base ${!companyWebsite.trim() ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Inizia una nuova mappatura
                                <ArrowRightIcon size={20} weight="bold" />
                            </Button>
                        </div>

                        {/* Recent Mappings */}
                        {(recentMappings.length > 0 || isLoadingRecent) && (
                            <div className="border-t border-black/10 pt-8">
                                <div className="flex items-center gap-2 mb-4">
                                    <ClockIcon size={20} weight="duotone" className="text-black/40" />
                                    <h2 className="text-lg font-semibold text-black/70">
                                        Mappature recenti
                                    </h2>
                                </div>

                                {isLoadingRecent ? (
                                    <div className="space-y-3">
                                        {[1, 2, 3].map((i) => (
                                            <div key={i} className="bg-black/5 rounded-xl h-16 animate-pulse" />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {recentMappings.map((mapping) => (
                                            <button
                                                key={mapping._id}
                                                onClick={() => navigate(`/tools/company-mapping/${mapping._id}`)}
                                                className="w-full flex items-center justify-between p-4 bg-white border border-black/10 rounded-xl hover:border-black/20 hover:shadow-sm transition-all text-left group"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center">
                                                        <MapTrifoldIcon size={20} weight="duotone" className="text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-black/80">
                                                            {mapping.name || 'Mappatura senza titolo'}
                                                        </p>
                                                        <p className="text-sm text-black/40">
                                                            {formatDate(mapping.createdAt)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <ArrowRightIcon
                                                    size={20}
                                                    className="text-black/30 group-hover:text-black/60 group-hover:translate-x-1 transition-all"
                                                />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
